import { Autocomplete, AutocompleteInputChangeReason, debounce, TextField } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CountryFragment, MunicipalityFragment } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { CountryMunicipality } from './country-municpality-lookup.types'
import useCountryMunicipalityLookupQuery, { useCountryMunicipalityFetchQuery } from './use-country-municipality-lookup-query'

export type CountryMunicipalityLookupProps = {
  value: CountryMunicipality | { countryId: string; municipalityId: string | null } | null
  onChange: (value: CountryMunicipality | null) => void
  disabled?: boolean
  error?: boolean
  size?: 'small' | 'medium'
}

type Option = CountryFragment | MunicipalityFragment

export default function CountryMunicipalityLookup({ value, onChange, disabled, error, size }: CountryMunicipalityLookupProps) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()

  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue, setDebouncedInputValue] = useState('')
  const debounceInputValue = useMemo(() => debounce((text: string) => setDebouncedInputValue(text), 300), [])
  const onInputChange = useCallback((event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    setInputValue(value)
    if (reason === 'input') {
      debounceInputValue(value)
    }
  }, [])

  const [options, setOptions] = useState<Option[]>([])
  const [italy, setItaly] = useState<CountryFragment | null>(null)
  const { data: searchData } = useCountryMunicipalityLookupQuery(debouncedInputValue)

  useEffect(() => {
    if (searchData) {
      const countries = searchData.countries || []
      const municipalities = [...(searchData.municipalities || [])]
      const eq = municipalities.find((m) => m.name === debouncedInputValue)
      if (eq) {
        municipalities.sort((prev, second) => {
          const prevFirst = prev.id === eq.id
          const secondFirst = second.id === eq.id
          return Number(secondFirst) - Number(prevFirst)
        })
      }
      setOptions([...countries, ...municipalities])
    }
    setItaly(searchData?.italy?.[0] ?? null)
  }, [searchData])

  const [localValue, setLocalValue] = useState<CountryFragment | MunicipalityFragment | null>(null)

  const localOnChange = useCallback(
    (option: CountryFragment | MunicipalityFragment | null) => {
      setLocalValue(option)
      if (option === null) return onChange(null)
      if ('isoCode' in option) {
        return onChange(option.isoCode !== 'IT' ? { country: option } : null)
      } else if (italy !== null) {
        return onChange({ country: italy, municipality: option })
      }
    },
    [onChange],
  )

  const localOptions = useMemo(() => {
    if (localValue === null || options.some((o) => o.id === localValue.id)) {
      return options
    } else {
      return [...options, localValue]
    }
  }, [options, localValue])

  const valueId: { countryId: string; municipalityId: string | null } | null = useMemo(() => {
    if (value === null) return null
    if (!('countryId' in value)) return null
    if (localValue === null) return { ...value }
    if ('isoCode' in localValue && localValue.isoCode !== 'IT' && localValue.id === value.countryId && value.municipalityId === null) return null
    if ('province' in localValue && localValue.id === value.municipalityId) return null
    return { ...value }
  }, [value])

  const { data: fetchData } = useCountryMunicipalityFetchQuery(valueId, valueId === null)

  useEffect(() => {
    if (fetchData) {
      const country = fetchData.countries?.[0] ?? null
      if (!country) {
        setLocalValue(null)
        onChange(null)
        return
      }
      const municipality = fetchData.municipalities?.[0]
      if (country.isoCode === 'IT' && !municipality) {
        setLocalValue(null)
        onChange(null)
        return
      }
      setLocalValue(municipality ?? country)
      onChange({ country, municipality })
    }
  }, [fetchData])

  useEffect(() => {
    if (value === null) {
      if (localValue !== null) {
        setLocalValue(null)
      }
      return
    }
    if ('countryId' in value) return
    if (value.municipality && JSON.stringify(value.municipality) != JSON.stringify(localValue)) {
      setLocalValue(value.municipality)
    } else if (JSON.stringify(value.country) != JSON.stringify(localValue)) {
      setLocalValue(value.country)
    }
  }, [value])

  return (
    <Autocomplete //
      size={size}
      fullWidth
      value={localValue}
      onChange={(event, value) => localOnChange(value)}
      options={localOptions}
      groupBy={(option) => {
        if ('isoCode' in option) {
          return t('pms.components.country_municipality.country_group')
        } else {
          return t('pms.components.country_municipality.municipaliry_group')
        }
      }}
      getOptionLabel={(option) => {
        if ('isoCode' in option) {
          return localizeString(option.name)
        } else {
          return `${option.name} (${option.province})`
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => <TextField {...params} error={error} />}
      filterOptions={(x) => x}
      onInputChange={onInputChange}
      disabled={disabled}
    />
  )
}
